(() => {
  'use strict';

  const DISPLAY = {
    recoveryWindow: {
      'next-day': 'Next day',
      '2-3-days': '2–3 days',
      '5-7-days': '5–7 days',
      'not-sure': "I'm not sure yet",
    },
    seoulTiming: {
      booked: 'My dates are booked',
      month: 'I know the month',
      exploring: "I'm still exploring",
    },
    outcome: {
      'look-rested': 'Look more rested',
      'tone-texture': 'Improve tone or texture',
      firmness: 'Improve firmness or definition',
      'help-defining': 'Help me define it',
    },
  };

  const QUESTION_STEPS = ['1', '2', '3'];

  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, (ch) => (
      { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]
    ));
  }

  if (!customElements.get('charter-preview')) {
    customElements.define(
      'charter-preview',
      class extends HTMLElement {
        connectedCallback() {
          this.answers = { recoveryWindow: '', seoulTiming: '', outcome: '' };
          this.currentStep = '1';
          this.panelOpen = false;
          this.reachedFinalStep = false; // reached the full-page Preview

          this.panel = this.querySelector('[data-cg-pv-panel]');

          this.addEventListener('click', this.onClick.bind(this));
          this.addEventListener('keydown', this.onKeydown.bind(this));

          // Any CTA anywhere on the page (header, hero, price section,
          // sticky CTA, or the in-section trigger) opens this same
          // contained panel, not a separate one. Bound once and removed in
          // disconnectedCallback so a Shopify section reload (theme editor
          // shopify:section:load) can't stack up duplicate document-level
          // listeners from stale, detached instances.
          this.boundOnDocumentClick = (evt) => {
            const opener = evt.target.closest('[data-cg-pv-open]');
            if (!opener) return;
            evt.preventDefault();
            this.open();
          };
          document.addEventListener('click', this.boundOnDocumentClick);

          this.render();

          // The launch-signup email lives inside its own real
          // `{% form 'customer' %}` (see sections/charterglow-landing.liquid)
          // so its result comes back as an ordinary full-page POST-redirect-
          // GET, not an AJAX response — Shopify's own posted_successfully?/
          // errors decide, server-side, whether [data-cg-launch-result]
          // exists in the very first render of this page. (`form` is only
          // in Liquid scope inside that form tag, so this marker element —
          // not an attribute on <charter-preview> itself — is how that
          // result reaches this component at all.) The panel starts closed
          // by default, so on its own a real result would render invisibly
          // behind [hidden]. Per Phase 2 R1 closeout §4.6, finding that
          // marker on load opens straight to it — no Q1-3 state is stored
          // or restored to make this happen.
          if (this.querySelector('[data-cg-launch-result]')) {
            this.open();
            this.goToStep('preview');
          }
        }

        disconnectedCallback() {
          if (this.boundOnDocumentClick) {
            document.removeEventListener('click', this.boundOnDocumentClick);
          }
        }

        // Privacy-safe funnel analytics only — step identifiers and reason
        // codes, never name, email, selected answer values, or anything
        // that could be read as a medical/aesthetic inference.
        emit(name, meta) {
          try {
            if (window.dataLayer && typeof window.dataLayer.push === 'function') {
              window.dataLayer.push(Object.assign({ event: name }, meta || {}));
            }
          } catch (e) {
            /* analytics must never break the flow */
          }
          this.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: meta || {} }));
        }

        onClick(evt) {
          const card = evt.target.closest('[data-pv-field]');
          if (card) {
            this.selectCard(card);
            return;
          }
          // The full-page Preview's Back returns to the landing flow
          // (closes the whole panel), not to Q3 — per the Build Brief,
          // this is a different action from the mid-questionnaire Back.
          if (evt.target.closest('[data-cg-pv-fp-back]')) {
            this.close();
            return;
          }
          if (evt.target.closest('[data-cg-pv-back]')) {
            this.goBack();
            return;
          }
          if (evt.target.closest('[data-cg-pv-close]')) {
            this.close();
            return;
          }
        }

        onKeydown(evt) {
          // Escape is the keyboard-equivalent "return to landing" control
          // for the full-page Preview, alongside the visible Back button.
          if (evt.key === 'Escape' && this.currentStep === 'preview') {
            this.close();
          }
        }

        open() {
          const wasOpen = this.panelOpen;
          this.panelOpen = true;
          this.panel.hidden = false;
          const startBtn = this.querySelector(':scope > [data-cg-pv-open]');
          if (startBtn) startBtn.hidden = true;
          this.panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
          window.dispatchEvent(new CustomEvent('cg-preview-toggle', { detail: { open: true } }));
          // Answers/step are preserved across close+reopen — this only
          // fires "panel opened" on a genuine first open, not a re-render.
          if (!wasOpen) {
            this.emit('cg_preview_panel_opened', {});
            if (QUESTION_STEPS.includes(this.currentStep)) {
              this.emit('cg_preview_question_step_reached', { step: this.currentStep });
            }
          }
          this.render(true);
        }

        close() {
          if (!this.panelOpen) return;
          this.panelOpen = false;
          this.panel.hidden = true;
          document.documentElement.classList.remove('cg-scroll-locked');
          const startBtn = this.querySelector(':scope > [data-cg-pv-open]');
          if (startBtn) startBtn.hidden = false;
          window.dispatchEvent(new CustomEvent('cg-preview-toggle', { detail: { open: false } }));
          // Step id only, never the answers themselves. reached_final_step
          // distinguishes "closed from the full-page Preview" from
          // "closed earlier", so the two aren't conflated in funnel
          // analytics.
          this.emit('cg_preview_panel_abandoned', {
            step: this.currentStep,
            reached_final_step: this.reachedFinalStep,
          });
        }

        selectCard(card) {
          const field = card.dataset.pvField;
          const value = card.dataset.pvValue;
          const label = card.dataset.pvLabel || card.textContent.trim();
          this.answers[field] = value;
          this.answers[`${field}Label`] = label;

          this.querySelectorAll(`[data-pv-field="${field}"]`).forEach((btn) => {
            btn.setAttribute('aria-pressed', btn === card ? 'true' : 'false');
          });

          const stepEl = card.closest('[data-pv-step]');
          const stepIndex = stepEl ? stepEl.dataset.pvStep : null;

          if (QUESTION_STEPS.includes(stepIndex)) {
            this.emit('cg_preview_question_step_completed', { step: stepIndex });
          }

          window.setTimeout(() => {
            if (stepIndex === '1') this.goToStep('2');
            else if (stepIndex === '2') this.goToStep('3');
            // Q3 leads straight to the full-page Preview — no name/email
            // gate in between, per the MVP Build Brief (§3.3).
            else if (stepIndex === '3') this.goToStep('preview');
          }, 180);
        }

        goBack() {
          const order = ['1', '2', '3'];
          const idx = order.indexOf(this.currentStep);
          if (idx > 0) this.goToStep(order[idx - 1]);
        }

        goToStep(step) {
          this.currentStep = step;
          if (QUESTION_STEPS.includes(step)) {
            this.emit('cg_preview_question_step_reached', { step });
          } else if (step === 'preview') {
            // Reaching the full-page Preview is the funnel's real
            // conversion point in this build (there's no submit step
            // before it) — step id only, no answer values.
            this.reachedFinalStep = true;
            this.emit('cg_preview_fullpage_reached', {});
          }
          this.render(true);
        }

        // Full-page Preview (section B: input summary, section E: decision
        // directions) — the only dynamic content in this step. Everything
        // else in the Preview (evidence lens, research records, paid
        // boundary) is static text sourced verbatim from
        // docs/research/PUBLIC_PREVIEW_RENDER_MANIFEST.md and needs no
        // per-visitor rendering.
        renderFullPreview() {
          // Normally starts at the cover, not wherever the reader scrolled
          // to last time (e.g. reopening after close, or arriving fresh).
          // Exception: a real launch-signup result (server-rendered by
          // Shopify's own form.posted_successfully?/errors after a full-
          // page submit — see connectedCallback) lives at the bottom of
          // this step, so that's brought into view and focused instead,
          // per Phase 2 R1 closeout §4.6 ("접근 가능한 결과 영역").
          const resultEl = this.querySelector('[data-cg-launch-result]');
          if (resultEl) {
            resultEl.scrollIntoView({ block: 'center' });
            if (typeof resultEl.focus === 'function') resultEl.focus();
          } else {
            const scrollEl = this.querySelector('[data-cg-pv-fp-scroll]');
            if (scrollEl) scrollEl.scrollTop = 0;
          }

          const list = this.querySelector('[data-cg-pv-fp-inputs]');
          if (list) {
            const rows = [
              ['Recovery window', DISPLAY.recoveryWindow[this.answers.recoveryWindow] || ''],
              ['Seoul timing', DISPLAY.seoulTiming[this.answers.seoulTiming] || ''],
              ['Desired outcome', DISPLAY.outcome[this.answers.outcome] || ''],
            ];
            list.innerHTML = rows
              .map((row) => `<li><span>${escapeHtml(row[0])}</span><span>${escapeHtml(row[1])}</span></li>`)
              .join('');
          }

          const recoveryTarget = this.querySelector('[data-cg-pv-fp-direction="recovery"]');
          if (recoveryTarget) {
            recoveryTarget.textContent = DISPLAY.recoveryWindow[this.answers.recoveryWindow] || '';
          }
          const outcomeTarget = this.querySelector('[data-cg-pv-fp-direction="outcome"]');
          if (outcomeTarget) {
            outcomeTarget.textContent = DISPLAY.outcome[this.answers.outcome] || '';
          }
        }

        render(focusStep) {
          this.querySelectorAll('[data-cg-pv-panel-step]').forEach((stepEl) => {
            stepEl.hidden = stepEl.dataset.pvStep !== this.currentStep;
          });

          if (this.currentStep === 'preview') this.renderFullPreview();

          // The full-page Preview is a fixed, full-viewport overlay — lock
          // the background page so it can't be scrolled underneath it.
          // Only that step needs this; the mid-questionnaire panel is
          // inline with the rest of the page.
          document.documentElement.classList.toggle(
            'cg-scroll-locked',
            this.panelOpen && this.currentStep === 'preview'
          );

          // Progress indicator only exists during the three questions —
          // removed once the flow reaches the full-page Preview, per spec.
          const progress = this.querySelector('[data-cg-pv-progress]');
          if (progress) {
            const showProgress = QUESTION_STEPS.includes(this.currentStep);
            progress.hidden = !showProgress;
            if (showProgress) {
              const stepNum = Number(this.currentStep);
              progress.querySelectorAll('[data-pv-progress-seg]').forEach((seg) => {
                const segNum = Number(seg.dataset.pvProgressSeg);
                seg.classList.toggle('is-complete', segNum < stepNum);
                seg.classList.toggle('is-current', segNum === stepNum);
              });
              const progressText = this.querySelector('[data-cg-pv-progress-text]');
              if (progressText) progressText.textContent = `Question ${stepNum} of 3`;
            }
          }

          // renderFullPreview() above already moved focus to the
          // launch-signup result marker when one exists — don't steal it
          // back to a heading or the (in that state, absent) email input.
          if (focusStep && !this.querySelector('[data-cg-launch-result]')) {
            const activeStep = this.querySelector(
              `[data-cg-pv-panel-step][data-pv-step="${this.currentStep}"]`
            );
            // A step with a real text input (the launch-signup email, when
            // enabled) gets focus landed directly in that field. Steps with
            // no text input (the three questions, the full-page Preview
            // with signup off) move focus to the heading instead, so
            // screen readers still announce the new step.
            const focusTarget =
              activeStep && (activeStep.querySelector('input:not([type="hidden"])') || activeStep.querySelector('h3, h2'));
            if (focusTarget && typeof focusTarget.focus === 'function') focusTarget.focus();
          }
        }
      }
    );
  }

  /* Aurora hero background — pauses when offscreen or the tab is hidden. CSS owns the motion. */
  (() => {
    const SELECTOR = '[data-cg-aurora]';

    // Per-hero intersecting state, since the preview-toggle event needs to
    // be able to force a pause/unpause independently of any intersection
    // change (the hero can still be geometrically in the viewport while the
    // full-page Preview overlay is covering it).
    const heroState = new WeakMap();
    let previewPanelOpen = false;

    const applyPauseState = (hero) => {
      const state = heroState.get(hero) || { intersecting: true };
      const shouldPause = !state.intersecting || document.hidden || previewPanelOpen;
      hero.classList.toggle('is-aurora-paused', shouldPause);
    };

    const setDocumentVisibility = () => {
      document.querySelectorAll(SELECTOR).forEach(applyPauseState);
    };

    const initAurora = (root = document) => {
      const heroes = root.matches?.(SELECTOR) ? [root] : Array.from(root.querySelectorAll?.(SELECTOR) || []);

      heroes.forEach((hero) => {
        if (hero.dataset.cgAuroraReady === 'true') return;
        hero.dataset.cgAuroraReady = 'true';

        if (!('IntersectionObserver' in window)) return;

        heroState.set(hero, { intersecting: true });

        const observer = new IntersectionObserver(
          ([entry]) => {
            heroState.set(hero, { intersecting: entry.isIntersecting });
            applyPauseState(hero);
          },
          { rootMargin: '180px 0px', threshold: 0 }
        );

        observer.observe(hero);
      });
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => initAurora());
    } else {
      initAurora();
    }

    document.addEventListener('visibilitychange', setDocumentVisibility);

    document.addEventListener('shopify:section:load', (event) => {
      initAurora(event.target);
    });

    // The full-page Preview overlay covers the hero without moving it out
    // of the viewport, so intersection alone can't detect that case — the
    // existing cg-preview-toggle event (also consumed by the sticky-CTA
    // logic below) is the source of truth for "is something occluding the
    // page right now."
    window.addEventListener('cg-preview-toggle', (evt) => {
      previewPanelOpen = !!(evt.detail && evt.detail.open);
      document.querySelectorAll(SELECTOR).forEach(applyPauseState);
    });
  })();

  /* Context-aware bottom sticky CTA — shown only once the hero CTA has left
     the viewport, hidden again near the Free Preview section and the
     footer, and hidden whenever the preview panel is open — so the header
     CTA (desktop only), hero CTA, and sticky CTA are never competing for
     attention at the same time. */
  (() => {
    const initContextualStickyCta = () => {
      const sticky = document.querySelector('.cg-sticky-cta');
      const heroCta = document.querySelector('.cg-hero .cg-cta');
      const previewSection = document.querySelector('#free-preview');
      const footer = document.querySelector('footer, .footer');

      if (!sticky || !heroCta || !previewSection) return;
      if (sticky.dataset.contextualStickyReady === 'true') return;
      sticky.dataset.contextualStickyReady = 'true';

      const state = {
        heroCtaVisible: true,
        previewSectionVisible: false,
        footerVisible: false,
        panelOpen: false,
      };

      const render = () => {
        const mobile = window.matchMedia('(max-width: 749px)').matches;
        const shouldShow =
          mobile &&
          !state.heroCtaVisible &&
          !state.previewSectionVisible &&
          !state.footerVisible &&
          !state.panelOpen;

        sticky.classList.toggle('is-visible', shouldShow);
        sticky.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
      };

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.target === heroCta) state.heroCtaVisible = entry.isIntersecting;
            if (entry.target === previewSection) state.previewSectionVisible = entry.isIntersecting;
            if (footer && entry.target === footer) state.footerVisible = entry.isIntersecting;
          });
          render();
        },
        { threshold: 0.05 }
      );

      observer.observe(heroCta);
      observer.observe(previewSection);
      if (footer) observer.observe(footer);

      window.addEventListener('cg-preview-toggle', (evt) => {
        state.panelOpen = !!(evt.detail && evt.detail.open);
        render();
      });

      const media = window.matchMedia('(max-width: 749px)');
      media.addEventListener?.('change', render);
      render();
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => initContextualStickyCta());
    } else {
      initContextualStickyCta();
    }

    document.addEventListener('shopify:section:load', () => {
      initContextualStickyCta();
    });
  })();
})();
